/**
 * AccountCodesGuide — User guide for Account Codes module.
 */
import React from 'react';
import GuideViewer, { GuideSection, GuideSubSection, GuideCallout, GuideFieldTable, GuideTOC } from './GuideViewer';
import AppShell from '../../components/layout/AppShell';

function GuideContent() {
  const toc = [
    { id: 'overview', label: 'What Are Account Codes?' },
    { id: 'structure', label: 'Code Structure & Segments' },
    { id: 'navigation', label: 'Navigating Account Codes' },
    { id: 'managing', label: 'Managing Code Values' },
    { id: 'usage', label: 'How Account Codes Are Used' },
    { id: 'reports', label: 'Account Code Reports' },
  ];

  return (
    <GuideViewer title="Account Codes Module" subtitle="Expenditure Code Structure User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. What Are Account Codes?">
        <p className="text-sm text-slate-600 mb-3">
          Account Codes (also called Expenditure Codes or Cost Codes) provide a structured way to classify and allocate procurement spend. They are a hierarchical coding system that maps to your organisation's chart of accounts or internal budget structure.
        </p>
        <p className="text-sm text-slate-600 mb-3">
          Every requisition and purchase order line item can be tagged with one or more account code segments, enabling precise spend tracking and budget control. For example, a laptop purchase might be coded as:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 mb-3">
          <li><strong>Segment 1 (Department)</strong>: IT-DEPT</li>
          <li><strong>Segment 2 (Cost Centre)</strong>: CC-LONDON-01</li>
          <li><strong>Segment 3 (GL Code)</strong>: 6200-CAPEX</li>
        </ul>
      </GuideSection>

      <GuideSection id="structure" title="2. Code Structure & Segments">
        <p className="text-sm text-slate-600 mb-3">
          Your system administrator defines the account code structure in <strong>System Administration &gt; Account Code Setup</strong>. The structure consists of up to 5 segments, each with a label and configuration:
        </p>
        <GuideFieldTable fields={[
          { name: 'Segment Number', desc: 'The position in the hierarchy (1, 2, 3, etc.). Segment 1 is the broadest level.' },
          { name: 'Segment Label', desc: 'The name displayed to users (e.g., "Department", "Cost Centre", "GL Code", "Project").' },
          { name: 'Mandatory', desc: 'Whether this segment must be filled in on every transaction line item.' },
          { name: 'Code Values', desc: 'The list of valid values for this segment (managed in the Account Codes module).' },
        ]} />
        <GuideCallout type="info" title="Segment Configuration">
          <p>The number of segments and their labels are configured by your administrator. Most organisations use 2-4 segments. The segment structure cannot be changed once transactions exist against it.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="navigation" title="3. Navigating Account Codes">
        <p className="text-sm text-slate-600 mb-3">
          Click <strong>Account Codes</strong> in the left sidebar. You will see a sub-menu showing each configured segment. Click a segment to view and manage its code values.
        </p>
        <p className="text-sm text-slate-600 mb-2">
          Each segment page shows a table of all values for that segment, with columns for:
        </p>
        <GuideFieldTable fields={[
          { name: 'Code', desc: 'The account code value (e.g., "IT-DEPT", "6200").' },
          { name: 'Description', desc: 'A human-readable label (e.g., "Information Technology Department").' },
          { name: 'Active', desc: 'Whether this value is available for selection on transactions.' },
          { name: 'Budget', desc: 'An optional budget amount allocated to this code, used for spend-vs-budget tracking.' },
        ]} />
      </GuideSection>

      <GuideSection id="managing" title="4. Managing Code Values">
        <GuideSubSection title="Adding a New Code">
          <p className="text-sm text-slate-600 mb-2">
            Click <strong>+ New</strong> above the table. Enter the code, description, and optionally a budget amount. Click <strong>Save</strong>.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Editing a Code">
          <p className="text-sm text-slate-600 mb-2">
            Click any row in the table to open the edit form. Modify the description or budget and click <strong>Save</strong>. The code value itself cannot be changed once created.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Deactivating a Code">
          <p className="text-sm text-slate-600 mb-2">
            Set the <strong>Active</strong> toggle to off. Deactivated codes remain in the system for historical reporting but cannot be selected on new transactions.
          </p>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="usage" title="5. How Account Codes Are Used">
        <p className="text-sm text-slate-600 mb-3">
          When creating a requisition or purchase order, each line item has account code fields corresponding to your configured segments. Users select values from dropdown lists for each segment.
        </p>
        <GuideCallout type="tip" title="Budget Tracking">
          <p>If budgets are assigned to account codes, the system tracks spend against budget and can display a budget chart on the requisition screen showing utilisation percentages.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="reports" title="6. Account Code Reports">
        <p className="text-sm text-slate-600 mb-3">
          Account Code reports are available from the <strong>Reports</strong> tab on each segment page. They allow you to:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>Export all codes with their budgets and descriptions</li>
          <li>Filter by active/inactive status</li>
          <li>Generate PDF reports for budget review meetings</li>
          <li>Export to CSV for analysis in spreadsheet tools</li>
        </ul>
      </GuideSection>
    </GuideViewer>
  );
}

export default function AccountCodesGuidePage() {
  return <AppShell><GuideContent /></AppShell>;
}
export function AccountCodesGuideEmbed() {
  return <GuideContent />;
}
